import React from 'react';
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Tooltip from "@mui/material/Tooltip";
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { IGetLessonWithVersionsResponse } from "api/models/response/IGetLessonWithVersionsResponse";
import {
    StyledListItem,
    VersionInfoBox,
    LanguageTypography
} from "components/views/editor/components/ManageLessonVersionsDialog.styles";

interface ManageLessonVersionsDialogProps {
    open: boolean;
    onClose: () => void;
    lesson: IGetLessonWithVersionsResponse;
    onSetDefault: (versionId: string) => void;
    onDelete: (language: string) => void;
    setting: boolean;
    deleting: boolean;
}

export const ManageLessonVersionsDialog: React.FC<ManageLessonVersionsDialogProps> = ({
                                                                              open,
                                                                              onClose,
                                                                              lesson,
                                                                              onSetDefault,
                                                                              onDelete,
                                                                              setting,
                                                                              deleting
                                                                          }) => {
    const { t } = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>{t('versionSelector.manageVersions')}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="textSecondary" paragraph>
                    {t('versionSelector.manageHelp')}
                </Typography>

                <List>
                    {lesson.versions.map((version) => (
                        <StyledListItem
                            key={version.language}
                        >
                            <ListItemText
                                primary={
                                    <VersionInfoBox>
                                        <LanguageTypography variant="subtitle1">
                                            {version.language.toUpperCase()}
                                        </LanguageTypography>
                                        {version.isDefault && (
                                            <Chip
                                                label={t('versionSelector.default')}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        )}
                                    </VersionInfoBox>
                                }
                            />
                            <ListItemSecondaryAction>
                                <Tooltip
                                    title={version.isDefault ? t('versionSelector.isDefault') : t('versionSelector.setAsDefault')}>
                                    <span>
                                        <IconButton
                                            onClick={() => onSetDefault(version.id)}
                                            disabled={version.isDefault || setting}
                                            color={version.isDefault ? "primary" : "default"}
                                        >
                                            {version.isDefault ? <StarIcon/> : <StarBorderIcon/>}
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                <Tooltip
                                    title={version.isDefault ? t('versionSelector.cannotDeleteDefault') : t('versionSelector.delete')}>
                                    <span>
                                        <IconButton
                                            onClick={() => onDelete(version.language)}
                                            disabled={version.isDefault || deleting}
                                            color="error"
                                        >
                                            <DeleteIcon/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </ListItemSecondaryAction>
                        </StyledListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {t('versionSelector.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};